import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"

// Companies with a Company Run, each with its latest one.
export const searchCompanies = createServerFn({ method: "GET" })
  .inputValidator(
    z
      .object({
        query: z.string().trim().min(2).max(200),
      })
      .strict()
  )
  .handler(async ({ data }) => {
    const [
      { assertAdminContext },
      { db },
      { desc, eq, ilike, isNotNull, and, sql },
      { terminalCompany },
      { terminalCompanyRun, terminalMonitoringRun },
    ] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/lib/admin-db"),
      import("drizzle-orm"),
      import("@/db/schema/terminal-company-identity"),
      import("@/db/schema/terminal-schema"),
    ])
    await assertAdminContext()
    return db.transaction(
      async (tx) => {
        const latest = tx
          .selectDistinctOn([terminalCompanyRun.company_id], {
            companyId: terminalCompanyRun.company_id,
            companyRunId: terminalCompanyRun.id,
            startedAt: sql<string>`${terminalMonitoringRun.started_at}`.as(
              "started_at"
            ),
          })
          .from(terminalCompanyRun)
          .innerJoin(
            terminalMonitoringRun,
            eq(terminalMonitoringRun.id, terminalCompanyRun.monitoring_run_id)
          )
          .orderBy(
            terminalCompanyRun.company_id,
            desc(terminalMonitoringRun.started_at),
            desc(terminalCompanyRun.id)
          )
          .as("latest")
        const pattern = `%${data.query.replace(/[\\%_]/g, "\\$&")}%`
        return tx
          .select({
            companyId: terminalCompany.id,
            displayName: terminalCompany.display_name,
            website: sql<string | null>`${terminalCompany.intake}->>'website'`,
            companyRunId: latest.companyRunId,
            startedAt: latest.startedAt,
          })
          .from(terminalCompany)
          .leftJoin(latest, eq(latest.companyId, terminalCompany.id))
          .where(
            and(
              ilike(terminalCompany.display_name, pattern),
              isNotNull(latest.companyRunId)
            )
          )
          .orderBy(terminalCompany.display_name)
          .limit(20)
      },
      { accessMode: "read only" }
    )
  })

export type CompanySearchResult = Awaited<
  ReturnType<typeof searchCompanies>
>[number]

export const loadCompanyRunHistory = createServerFn({ method: "GET" })
  .inputValidator(
    z
      .object({
        companyRunId: z.string().uuid(),
        limit: z.number().int().min(50).max(1000).default(50),
      })
      .strict()
  )
  .handler(async ({ data }) => {
    const [
      { assertAdminContext },
      { db },
      { desc, eq, inArray },
      { terminalCompanyRun, terminalMonitoringRun },
    ] = await Promise.all([
      import("@/lib/supabase/auth"),
      import("@/lib/admin-db"),
      import("drizzle-orm"),
      import("@/db/schema/terminal-schema"),
    ])
    await assertAdminContext()
    return db.transaction(
      (tx) =>
        tx
          .select({
            companyRunId: terminalCompanyRun.id,
            monitoringRunId: terminalMonitoringRun.id,
            startedAt: terminalMonitoringRun.started_at,
            outcome: terminalCompanyRun.outcome,
          })
          .from(terminalCompanyRun)
          .innerJoin(
            terminalMonitoringRun,
            eq(terminalMonitoringRun.id, terminalCompanyRun.monitoring_run_id)
          )
          .where(
            inArray(
              terminalCompanyRun.company_id,
              tx
                .select({ companyId: terminalCompanyRun.company_id })
                .from(terminalCompanyRun)
                .where(eq(terminalCompanyRun.id, data.companyRunId))
            )
          )
          .orderBy(
            desc(terminalMonitoringRun.started_at),
            desc(terminalCompanyRun.id)
          )
          .limit(data.limit),
      { accessMode: "read only" }
    )
  })
